import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Attaches the access token to API requests and transparently refreshes it once
 * on a 401 response. Auth endpoints are skipped to avoid recursion.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const isAuthCall = req.url.includes('/auth/');

  const token = auth.getAccessToken();
  const authReq =
    token && !isAuthCall
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !isAuthCall) {
        return from(auth.refresh()).pipe(
          switchMap((ok) => {
            if (!ok) {
              // Only log out when the session is truly dead; a transient refresh
              // failure (rate limit, network blip) keeps the stored token.
              if (!auth.hasStoredSession()) {
                auth.logout();
              }
              return throwError(() => err);
            }
            const retry = req.clone({
              setHeaders: { Authorization: `Bearer ${auth.getAccessToken()}` }
            });
            return next(retry);
          })
        );
      }
      return throwError(() => err);
    })
  );
};
